/**
 * Central config for all 8 service categories.
 * QE fees are defaults. Admin can override via /api/settings (keys: qe_{slug}_fee_small, etc.)
 */

export const CATEGORIES = [
  {
    slug: 'malning',
    route: '/målning',
    label: 'Målning & tapetsering',
    icon: 'Paintbrush',
    shortLabel: 'Målning',
    seoTitle: 'Vad kostar målning och tapetsering? Räkna ut pris | Klart Hemma',
    seoDescription: 'Målning och tapetsering som ger rummet ett nytt uttryck. Beskriv ytan och arbetet så får du en prisindikation att utgå från.',
    trustBullets: [
      'Målning av väggar, tak och snickerier',
      'Tapetsering och förarbete efter behov',
      'Prisindikation utifrån rummets storlek och arbetets omfattning',
    ],
    faq: [
      { q: 'Ingår material i priset?', a: 'Det beror på vad ni kommer överens om. Ange om du redan har färg eller tapet så kan materialet specificeras i offerten.' },
      { q: 'Hur lång tid tar målning av ett rum?', a: 'Det beror på underlag, förarbete, yta och antal strykningar. Vi stämmer av tidplanen när vi vet vad som ska målas.' },
      { q: 'Vad är ROT-avdrag och hur funkar det?', a: 'För godkänt ROT-arbete kan avdraget vara upp till 30 % av arbetskostnaden. Material ingår inte, och dina villkor avgör hur mycket du kan använda.' },
      { q: 'Kan ni hjälpa till med tapetsering också?', a: 'Ja, välj tapetsering i guiden och beskriv om befintlig tapet behöver tas bort. Underlaget avgör vilket förarbete som behövs.' },
      { q: 'Hur lång tid i förväg behöver jag boka?', a: 'Kontakta oss med önskad tid och omfattning så stämmer vi av tillgängligheten för just ditt projekt.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 8900,
      feeStandard: 18900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.80,
      q1Label: 'Hur stort är utrymmet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 15 m²' },
        { key: 'standard', label: 'Standard', sub: '15-40 m²' },
        { key: 'large', label: 'Stort', sub: '40+ m²' },
      ],
      q2Label: 'Vad ska göras?',
      q2Options: [
        { key: 'vagg', label: 'Väggar', multiplier: 1.00 },
        { key: 'tak', label: 'Tak', multiplier: 0.85 },
        { key: 'bagg_tak', label: 'Väggar + tak', multiplier: 1.20 },
        { key: 'tapetsera', label: 'Tapetsera', multiplier: 1.15 },
      ],
    },
    wizardParams: {
      min_hours_median: 8,
      paint_wall_h_per_m2: 0.55,
      paint_ceiling_h_per_m2: 0.45,
      paint_trim_h_per_meter: 0.08,
      wallpaper_h_per_m2: 0.22,
      strip_wallpaper_h_per_m2: 0.18,
    },
  },
  {
    slug: 'kok',
    route: '/kök',
    label: 'Köksrenovering',
    icon: 'ChefHat',
    shortLabel: 'Kök',
    seoTitle: 'Vad kostar köksrenovering? Räkna ut pris | Klart Hemma',
    seoDescription: 'Planerar du att byta kök? Beskriv stommar, bänkskiva och tillval för en första prisindikation på arbetet.',
    trustBullets: [
      'Välj stommar, bänkskiva och vitvaror som är aktuella',
      'Ange om det gamla köket ska rivas',
      'ROT kan gälla för godkänd arbetskostnad',
    ],
    faq: [
      { q: 'Ingår köket i priset?', a: 'Köksinredning och montage är olika kostnader. Offerten behöver visa vad som ingår i just ditt projekt.' },
      { q: 'Hur lång tid tar ett köksbyte?', a: 'Det beror på rivning, leveranser, installationer och hur köket ser ut. En tidplan kan tas fram när omfattningen är klar.' },
      { q: 'Kan ni installera el och VVS?', a: 'Markera el och VVS i guiden om det behövs. Då kan ansvar, omfattning och eventuella behörighetskrav klargöras före offert.' },
      { q: 'Vad kostar ett standardkök att montera?', a: 'Antal stommar, bänkskiva, vitvaror och förarbete påverkar priset. Beskriv köket i guiden för en preliminär prisbild.' },
      { q: 'Ingår städning efteråt?', a: 'Det ska framgå av offerten vad som ingår i avslut och städning. Be oss förtydliga den punkten innan du bestämmer dig.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 29900,
      feeStandard: 39900,
      feeLarge: 54900,
      spanDefault: 0.25,
      rotLaborShare: 0.85,
      q1Label: 'Hur stort är köket?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 7 skåp' },
        { key: 'standard', label: 'Standard', sub: '8-11 skåp' },
        { key: 'large', label: 'Stort', sub: '12+ skåp' },
      ],
      q2Label: 'Antal löpande meter bänk',
      q2Options: [
        { key: 'k6', label: '6 m', multiplier: 0.85 },
        { key: 'k8', label: '8 m', multiplier: 1.00 },
        { key: 'k10', label: '10 m', multiplier: 1.10 },
        { key: 'k12', label: '12 m', multiplier: 1.20 },
        { key: 'k14plus', label: '14+ m', multiplier: 1.35 },
      ],
    },
    wizardParams: {
      backsplash_h_per_meter: 4.0,
      backsplash_extra_gips_h: 4.0,
      backsplash_extra_concrete_h: 1.0,
      backsplash_extra_unknown_h: 2.5,
      demolition_fee_small: 6000,
      demolition_fee_standard: 7500,
      demolition_fee_large: 9000,
      disposal_fee_per_run: 5000,
    },
  },
  {
    slug: 'vatrum',
    route: '/våtrum',
    label: 'Våtrumsrenovering',
    icon: 'ShowerHead',
    shortLabel: 'Våtrum',
    seoTitle: 'Vad kostar badrumsrenovering? Räkna ut pris | Klart Hemma',
    seoDescription: 'Planerar du badrum eller tvättstuga? Beskriv yta, tätskikt och omfattning för en första prisindikation.',
    trustBullets: [
      'Välj mellan ytskikt, delrenovering och helrenovering',
      'Beskriv mått och vad som ska behållas',
      'Tätskikt och installationer stäms av före offert',
    ],
    faq: [
      { q: 'Vad kostar en helrenovering av badrum?', a: 'Prisbilden beror på yta, befintligt skick, tätskikt, installationer och materialval. Guiden ger en uppskattning som behöver stämmas av.' },
      { q: 'Hur lång tid tar en badrumsrenovering?', a: 'Tidplanen beror på rivning, underlag, torktider och arbetets omfattning. Den kan bedömas när förutsättningarna är kända.' },
      { q: 'Ingår VVS och el i priset?', a: 'Det beror på projektet. Beskriv vad som ska flyttas eller bytas så att el och VVS kan specificeras i offerten.' },
      { q: 'Vad är tätskikt och måste det bytas?', a: 'Tätskikt skyddar konstruktionen mot vatten. Vad som behöver göras avgörs av befintligt utförande och renoveringens omfattning.' },
      { q: 'Kan ni renovera utan att riva allt?', a: 'I vissa rum kan en delrenovering vara möjlig. Befintliga tätskikt och anslutningar måste bedömas innan omfattningen bestäms.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 69000,
      feeStandard: 119000,
      feeLarge: 179000,
      spanDefault: 0.30,
      rotLaborShare: 0.55,
      q1Label: 'Hur stort är badrummet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 5 m²' },
        { key: 'standard', label: 'Standard', sub: '5-10 m²' },
        { key: 'large', label: 'Stort', sub: '10+ m²' },
      ],
      q2Label: 'Vad ingår i renoveringen?',
      q2Options: [
        { key: 'ytskikt', label: 'Ytskikt', multiplier: 0.85 },
        { key: 'del', label: 'Delrenovering', multiplier: 1.00 },
        { key: 'hel', label: 'Helrenovering', multiplier: 1.25 },
      ],
    },
    wizardParams: {
      wet_unknown_multiplier: 1.15,
      wet_m2_fee: 8500,
    },
  },
  {
    slug: 'kakel',
    route: '/kakel',
    label: 'Kakel & klinker',
    icon: 'LayoutGrid',
    shortLabel: 'Kakel',
    seoTitle: 'Vad kostar kakelsättning? Räkna ut pris | Klart Hemma',
    seoDescription: 'Beskriv kakel- eller klinkerarbete, yta och underlag för en första prisindikation.',
    trustBullets: [
      'Välj vägg, golv eller båda',
      'Ange yta och typ av platta',
      'Underlag och eventuell rivning påverkar omfattningen',
    ],
    faq: [
      { q: 'Vad kostar kakelsättning per kvadratmeter?', a: 'Yta, plattformat, underlag, hörn och förarbete påverkar priset. Beskriv dessa delar för en mer användbar prisindikation.' },
      { q: 'Ingår rivning av gammalt kakel?', a: 'Rivning behöver anges separat. Skriv vad som finns i dag så kan omfattningen tas med i offerten.' },
      { q: 'Hur lång tid tar kakelsättning i ett kök?', a: 'Tiden beror på yta, underlag, plattformat och torktider. En tidplan kan tas fram när material och förarbete är kända.' },
      { q: 'Vilken typ av kakel är svårast att lägga?', a: 'Små plattor, stora format och många hörn kan kräva mer arbete. Valet behöver bedömas tillsammans med underlaget.' },
      { q: 'Ingår fukt/tätskikt?', a: 'I ett våtrum behöver tätskikt och anslutningar ingå i planeringen. Offerten ska ange om det arbetet ingår.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 12900,
      feeStandard: 24900,
      feeLarge: 44900,
      spanDefault: 0.25,
      rotLaborShare: 0.65,
      q1Label: 'Hur stor yta?',
      q1Options: [
        { key: 'small', label: 'Liten', sub: 'upp till 10 m²' },
        { key: 'standard', label: 'Standard', sub: '10-25 m²' },
        { key: 'large', label: 'Stor', sub: '25+ m²' },
      ],
      q2Label: 'Var ska kakel läggas?',
      q2Options: [
        { key: 'vagg', label: 'Vägg', multiplier: 1.00 },
        { key: 'golv', label: 'Golv', multiplier: 0.95 },
        { key: 'bagg_golv', label: 'Vägg + golv', multiplier: 1.35 },
      ],
    },
    wizardParams: {
      min_hours_median: 8,
      tile_wall_h_per_m2: 1.25,
      tile_floor_h_per_m2: 1.10,
      tile_demo_h_per_m2: 0.60,
      corner_h_each: 0.35,
      mosaic_multiplier: 1.35,
      large_tile_multiplier: 1.15,
      concrete_tile_multiplier: 1.15,
    },
  },
  {
    slug: 'golv',
    route: '/golv',
    label: 'Golvläggning',
    icon: 'Layers',
    shortLabel: 'Golv',
    seoTitle: 'Vad kostar golvläggning? Räkna ut pris | Klart Hemma',
    seoDescription: 'Planerar du nytt golv eller slipning? Beskriv yta, material och underlag för en första prisindikation.',
    trustBullets: [
      'Välj nytt golv eller slipning',
      'Ange yta, material och befintligt underlag',
      'Lister och rivning kan beskrivas separat',
    ],
    faq: [
      { q: 'Vad kostar golvläggning per kvadratmeter?', a: 'Golvtyp, yta, underlag och eventuellt förarbete påverkar priset. Guiden ger en uppskattning att stämma av.' },
      { q: 'Ingår rivning av gammalt golv?', a: 'Ange om det gamla golvet ska tas bort. Rivning och bortforsling behöver framgå av offerten.' },
      { q: 'Kan ni slipa parkettgolv?', a: 'Slipning finns som val i guiden. Beskriv golvets skick och önskad ytbehandling så går det att bedöma arbetet.' },
      { q: 'Vad är skillnaden på laminat och vinyl?', a: 'Materialen har olika uppbyggnad och krav på underlag. Fukt- och slitstyrka varierar mellan produkter; följ tillverkarens anvisningar för rummet.' },
      { q: 'Ingår lister i priset?', a: 'Ange om socklar eller andra lister ska bytas. Det ska stå i offerten om de ingår.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 9900,
      feeStandard: 19900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.70,
      q1Label: 'Hur stor yta?',
      q1Options: [
        { key: 'small', label: 'Liten', sub: 'upp till 20 m²' },
        { key: 'standard', label: 'Standard', sub: '20-50 m²' },
        { key: 'large', label: 'Stor', sub: '50+ m²' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'lagga', label: 'Lägga nytt golv', multiplier: 1.00 },
        { key: 'slipa', label: 'Slipa befintligt', multiplier: 1.10 },
        { key: 'matta', label: 'Heltäckningsmatta', multiplier: 0.95 },
      ],
    },
    wizardParams: {
      min_hours_median: 6,
      install_wood_h_per_m2: 0.28,
      install_laminate_h_per_m2: 0.24,
      install_vinyl_h_per_m2: 0.22,
      install_carpet_h_per_m2: 0.25,
      remove_h_per_m2: 0.18,
      sand_h_per_m2: 0.30,
      finish_lacquer_h_per_m2: 0.08,
      finish_oil_h_per_m2: 0.06,
      baseboard_h_per_m: 0.10,
    },
  },
  {
    slug: 'vvs',
    route: '/vvs',
    label: 'VVS',
    icon: 'Wrench',
    shortLabel: 'VVS',
    seoTitle: 'Pris på VVS-arbete: inkoppling och flytt | Klart Hemma',
    seoDescription: 'Beskriv planerat VVS-arbete, plats och omfattning för en första prisindikation eller återkoppling.',
    trustBullets: [
      'Välj inkoppling, byte, stopp eller flytt',
      'Beskriv befintliga anslutningar och åtkomst',
      'Arbetets omfattning stäms av före offert',
    ],
    faq: [
      { q: 'Vad kostar byte av blandare?', a: 'Typ av blandare, befintliga anslutningar och åtkomst påverkar arbetet. Beskriv förutsättningarna för en prisindikation.' },
      { q: 'Kan ni hjälpa till vid stopp i avlopp?', a: 'Beskriv var stoppet sitter och om problemet återkommer. Vi behöver bedöma arbetets omfattning innan pris eller tid kan bekräftas.' },
      { q: 'Vad kostar det att flytta ett element?', a: 'Rördragning, avstånd och åtkomst avgör omfattningen. Bifoga gärna en enkel skiss eller foto i guiden.' },
      { q: 'Ingår material i priset?', a: 'Material och arbete ska specificeras i offerten. Ange om du redan har en armatur eller vill ha hjälp att välja.' },
      { q: 'Hur snabbt kan ni komma vid akut läckage?', a: 'Formuläret är för planerade arbeten och ger ingen jourtid. Vid pågående läckage: stäng av vattnet och kontakta en lokal jour.' },
    ],
    subcategoryCards: [
      { key: 'inkoppling', label: 'Inkoppling', sub: 'Ny diskho, blandare, dusch', icon: 'Droplet' },
      { key: 'byte', label: 'Byte av armatur', sub: 'Blandare, toalett, dusch', icon: 'RefreshCw' },
      { key: 'stopp', label: 'Stopp / Läckage', sub: 'Rensning, tätning', icon: 'Droplets' },
      { key: 'flytt', label: 'Flytt / Omdragning', sub: 'Rörflytt, element', icon: 'ArrowLeftRight' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 3900,
      feeStandard: 7900,
      feeLarge: 14900,
      spanDefault: 0.25,
      rotLaborShare: 0.60,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'enkelt byte / stopp' },
        { key: 'standard', label: 'Standard', sub: 'inkoppling / anslutning' },
        { key: 'large', label: 'Stort', sub: 'flytt / renovering' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'inkoppling_byte', label: 'Inkoppling / Byte', multiplier: 1.00 },
        { key: 'stopp', label: 'Stopp / Läckage', multiplier: 0.95 },
        { key: 'flytt', label: 'Flytt / Omdragning', multiplier: 1.60 },
      ],
    },
    wizardParams: {
      fee_connect: 3900,
      fee_connect_medium: 6900,
      fee_connect_large: 9900,
      fee_replace_fixture: 3500,
      fee_drain_small: 2900,
      fee_drain_large: 5900,
      fee_move_small: 8900,
      fee_move_medium: 12900,
      fee_move_large: 18900,
      subcontractor_markup_multiplier: 1.30,
    },
  },
  {
    slug: 'el',
    route: '/el',
    label: 'Elinstallation',
    icon: 'Zap',
    shortLabel: 'El',
    seoTitle: 'Pris på elinstallation: uttag och belysning | Klart Hemma',
    seoDescription: 'Beskriv uttag, belysning eller annan elinstallation för en första bedömning av arbetet.',
    trustBullets: [
      'Välj uttag, belysning, ny grupp eller felsökning',
      'Beskriv befintlig installation och önskad ändring',
      'Förutsättningar och behörighet kontrolleras före arbete',
    ],
    faq: [
      { q: 'Vad kostar ett nytt uttag?', a: 'Prisbilden beror på kabeldragning, väggmaterial, placering och befintlig installation. Beskriv vad du vill ändra i guiden.' },
      { q: 'Kan ni hjälpa till med downlights eller spottar?', a: 'Beskriv antal, placering och befintlig belysning. Arbetets omfattning och vem som får utföra det behöver stämmas av före offert.' },
      { q: 'Vad innebär en ny grupp?', a: 'En ny grupp innebär en separat matning från elcentralen. Behovet ska bedömas utifrån den befintliga anläggningen och utrustningen som ska anslutas.' },
      { q: 'Kan ni hjälpa till med felsökning?', a: 'Beskriv felet och när det uppstår. Vi återkommer om hur arbetet kan bedömas; använd inte formuläret för akuta säkerhetsrisker.' },
      { q: 'Ingår material i priset?', a: 'Material och arbete ska specificeras var för sig i offerten. Ange om du redan har armaturer eller uttag.' },
    ],
    subcategoryCards: [
      { key: 'inkoppling', label: 'Inkoppling', sub: 'Spis, torktumlare, laddbox', icon: 'Plug' },
      { key: 'uttag', label: 'Uttag / Armatur', sub: 'Nytt uttag eller armatur', icon: 'Lightbulb' },
      { key: 'belysning', label: 'Belysning / Spottar', sub: 'Inbyggd belysning', icon: 'Sun' },
      { key: 'ny_grupp', label: 'Ny grupp / Matning', sub: 'Ny säkringsgrupp', icon: 'Zap' },
      { key: 'felsok', label: 'Felsökning', sub: 'Hitta och åtgärda elfel', icon: 'Search' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 2900,
      feeStandard: 6900,
      feeLarge: 12900,
      spanDefault: 0.25,
      rotLaborShare: 0.60,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'enstaka uttag / armatur' },
        { key: 'standard', label: 'Standard', sub: 'rum / lösning' },
        { key: 'large', label: 'Stort', sub: 'ny installation / flera rum' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'uttag_armatur', label: 'Uttag / Armatur', multiplier: 1.00 },
        { key: 'belysning', label: 'Belysning / Spottar', multiplier: 1.15 },
        { key: 'ny_grupp', label: 'Ny grupp / Matning', multiplier: 1.60 },
        { key: 'felsok', label: 'Felsökning', multiplier: 1.00 },
      ],
    },
    wizardParams: {
      fee_basic: 2900,
      fee_outlet_extra: 1500,
      fee_lighting: 3900,
      fee_new_circuit: 6900,
      fee_troubleshoot: 2900,
      wall_open_multiplier: 1.20,
      long_run_multiplier: 1.15,
      concrete_multiplier: 1.15,
      subcontractor_markup_multiplier: 1.30,
    },
  },
  {
    slug: 'snickeri',
    route: '/snickeri',
    label: 'Snickeri',
    icon: 'Hammer',
    shortLabel: 'Snickeri',
    seoTitle: 'Pris på snickeri: innerväggar, lister och dörrar | Klart Hemma',
    seoDescription: 'Planerar du innervägg, lister, dörrar eller undertak? Beskriv omfattningen för en första prisindikation.',
    trustBullets: [
      'Välj vägg, tak, lister, dörrar eller ombyggnad',
      'Ange mått och vad som finns på plats i dag',
      'Ytbehandling och andra tillval stäms av separat',
    ],
    faq: [
      { q: 'Vad kostar det att sätta upp en innervägg?', a: 'Längd, höjd, material, dörröppningar och ytbehandling påverkar priset. Ange gärna mått i guiden.' },
      { q: 'Kan ni montera nya innerdörrar?', a: 'Innerdörrar och karmar finns som val i guiden. Beskriv antal och befintliga öppningar så går arbetet att bedöma.' },
      { q: 'Ingår spackling och målning?', a: 'Det beror på vilken färdig nivå du behöver. Ange ytbehandling separat och kontrollera sedan vad som står i offerten.' },
      { q: 'Kan ni bygga ett undertak?', a: 'Undertak finns som val i guiden. Ange yta, höjd och materialönskemål för en första bedömning.' },
      { q: 'Vad innebär en "mindre ombyggnad"?', a: 'Det kan vara flera arbeten i samma rum, exempelvis en ny innervägg eller flytt av en dörr. Beskriv vad som ska ändras så att omfattningen kan avgränsas.' },
    ],
    subcategoryCards: [
      { key: 'innervagg', label: 'Innervägg', sub: 'Regelvägg med gips', icon: 'Columns2' },
      { key: 'undertak', label: 'Undertak', sub: 'Gips- eller mineralullstak', icon: 'PanelTop' },
      { key: 'lister_socklar', label: 'Lister & socklar', sub: 'Golvsocklar och taklister', icon: 'Ruler' },
      { key: 'dorrar_foder', label: 'Dörrar & foder', sub: 'Innerdörrar och karmar', icon: 'DoorOpen' },
      { key: 'ombyggnad', label: 'Mindre ombyggnad', sub: 'Flytt av dörrar, öppning', icon: 'Hammer' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 7900,
      feeStandard: 17900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.75,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'lister, enstaka dörr' },
        { key: 'standard', label: 'Standard', sub: 'vägg eller tak' },
        { key: 'large', label: 'Stort', sub: 'ombyggnad / flera rum' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'vagg_undertak', label: 'Vägg / Undertak', multiplier: 1.15 },
        { key: 'lister_dorrar', label: 'Lister / Dörrar', multiplier: 1.00 },
        { key: 'ombyggnad', label: 'Ombyggnad', multiplier: 1.60 },
      ],
    },
    wizardParams: {
      partition_h_per_m: 1.20,
      ceiling_h_per_m2: 0.55,
      trim_h_per_m: 0.10,
      interior_door_h: 2.5,
      exterior_door_h: 5.0,
      casing_h_per_door: 1.5,
      fee_remodel_small: 12900,
      fee_remodel_medium: 24900,
      fee_remodel_large: 39900,
    },
  },
];

/** Lookup by slug */
export function getCategoryBySlug(slug) {
  return CATEGORIES.find((c) => c.slug === slug) || null;
}

/** Map of slug → route for easy reverse lookup */
export const CATEGORY_ROUTE_MAP = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c.route])
);

/** Default global settings used by QE when /api/settings is unavailable */
export const QE_DEFAULTS = {
  ROT_RATE: 0.30,
  hourly_private_inc_vat: 875,
  hourly_company_ex_vat: 550,
};
